"use client";

import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import { Row, Col, Button, Card, Table, Form, Modal, Alert, Container, Badge } from "react-bootstrap";
import { useNavigate } from "react-router";
import { WebSocketContext } from "../../websocket/websocket";
import { DataContext, useTrackedValues } from "../../storage/accessors";
import useWheelId from "../../hooks/useWheelId";
import { ForwardRefEditor } from "../../components/markdown/ForwardRefEditor";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Icon from "../../components/Icon";
import { faPlus as add, faArrowLeft, faTrash, faEdit, faShareNodes, faExclamationTriangle, faCompress, faExpand, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { getCsrfToken } from "../../components/utils/csrf";
import type { MDXEditorMethods } from '@mdxeditor/editor';
import { format, subDays, subMonths, startOfDay, startOfWeek, startOfMonth, endOfMonth, addDays } from "date-fns";
import { Line, Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    Filler
} from "chart.js";
import 'chartjs-adapter-date-fns';
import { ru } from "date-fns/locale";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    Filler
);

const translitMap: Record<string, string> = {
    'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo', 'ж': 'zh',
    'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm', 'н': 'n', 'о': 'o',
    'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u', 'ф': 'f', 'х': 'kh', 'ц': 'ts',
    'ч': 'ch', 'ш': 'sh', 'щ': 'shch', 'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu',
    'я': 'ya',
};

function titleToSlug(title: string): string {
    return title
        .toLowerCase()
        .split('')
        .map(ch => translitMap[ch] ?? ch)
        .join('')
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

interface TrackerChartProps {
    trackerUuid: string;
    lifePartColor: string;
    trackerName: string;
    measure: string;
    target?: number;
    targetIndicator?: 'MORE' | 'LESS';
    display?: 'SIMPLE' | 'SUM' | 'AVG';
    group?: 'NO' | 'WEEK' | 'MONTH';
    chartMode?: 'LAST14' | 'ALL';
}

function TrackerChart({ trackerUuid, lifePartColor, trackerName, measure, target, targetIndicator, display = 'SIMPLE', group = 'NO', chartMode: initialChartMode = 'LAST14' }: TrackerChartProps) {
    const trackedValues = useTrackedValues(trackerUuid);
    const [chartMode, setChartMode] = useState<'LAST14' | 'ALL'>(initialChartMode);

    const isSimple = display === "SIMPLE";
    const hasTarget = target !== undefined;

    const isTargetReached = useCallback((value: number): boolean => {
        if (target === undefined) return false;
        if (targetIndicator === "MORE") {
            return value >= target;
        } else {
            return value <= target;
        }
    }, [target, targetIndicator]);

    const currentPeriod = useMemo(() => {
        const now = new Date();
        
        if (group === "NO") {
            const today = startOfDay(now);
            return {
                start: today.getTime(),
                end: today.getTime() + 24 * 60 * 60 * 1000 - 1
            };
        }
        
        const groupByWeek = group === "WEEK";
        
        if (groupByWeek) {
            const weekStart = startOfWeek(now, { weekStartsOn: 1 });
            const weekEnd = addDays(weekStart, 6);
            return {
                start: startOfDay(weekStart).getTime(),
                end: weekEnd.getTime()
            };
        } else {
            const monthStart = startOfMonth(now);
            const monthEnd = endOfMonth(now);
            return {
                start: monthStart.getTime(),
                end: monthEnd.getTime()
            };
        }
    }, [group]);

    const isCurrentPeriod = useCallback((timestamp: number): boolean => {
        return timestamp >= currentPeriod.start && timestamp <= currentPeriod.end;
    }, [currentPeriod]);

    const chartData = useMemo(() => {
        if (isSimple) {
            const sortedValues = [...trackedValues].sort((a, b) => a.timestamp - b.timestamp);

            let days: { date: Date; timestamp: number; label: string }[];

            if (chartMode === "ALL") {
                if (sortedValues.length === 0) {
                    days = [];
                } else {
                    const firstDay = startOfDay(new Date(sortedValues[0].timestamp));
                    const lastDay = startOfDay(new Date());
                    const dayCount = Math.min(Math.ceil((lastDay.getTime() - firstDay.getTime()) / (24 * 60 * 60 * 1000)) + 1, 365);
                    days = Array.from({ length: dayCount }, (_, i) => {
                        const date = addDays(firstDay, i);
                        return {
                            date,
                            timestamp: date.getTime(),
                            label: format(date, "dd.MM")
                        };
                    });
                }
            } else {
                days = Array.from({ length: 14 }, (_, i) => {
                    const date = subDays(startOfDay(new Date()), 13 - i);
                    return {
                        date,
                        timestamp: date.getTime(),
                        label: format(date, "dd.MM")
                    };
                });
            }

            const dataPoints = days.map((day) => {
                const value = sortedValues.find(
                    (tv) => startOfDay(new Date(tv.timestamp)).getTime() === day.timestamp
                );
                return value !== undefined ? value.value : null;
            });

            const pointColors = dataPoints.map((value) => {
                if (value === null) {
                    return `${lifePartColor}40`;
                }
                if (!hasTarget) return lifePartColor;
                return isTargetReached(value) ? '#28a745' : '#dc3545';
            });

            const datasets: any[] = [
                {
                    label: trackerName,
                    data: dataPoints,
                    borderColor: lifePartColor,
                    backgroundColor: `${lifePartColor}40`,
                    tension: 0.3,
                    fill: 'origin',
                    spanGaps: true,
                    // В режиме «все точек» линия перегружена, поэтому точки не рисуем (тултип по ховеру остаётся)
                    pointRadius: chartMode === "ALL" ? 0 : 5,
                    pointHoverRadius: chartMode === "ALL" ? 5 : 7,
                    pointBackgroundColor: pointColors,
                    pointBorderColor: pointColors.map(c =>
                        c === '#28a745' ? '#1e7e34' :
                        c === '#dc3545' ? '#bd2130' :
                        c === `${lifePartColor}40` ? c : '#fff'
                    ),
                    pointBorderWidth: 2
                }
            ];

            if (hasTarget) {
                datasets.push({
                    label: 'Цель',
                    data: Array(days.length).fill(target),
                    borderColor: '#6c757d',
                    borderDash: [5, 5],
                    borderWidth: 2,
                    pointRadius: 0,
                    fill: false,
                    tension: 0
                });
            }

            return {
                labels: days.map((d) => d.label),
                datasets
            };
        } else {
            const groupByWeek = group === "WEEK";
            const now = new Date();

            let periods: { start: number; end: number; label: string }[];

            if (chartMode === "ALL") {
                if (trackedValues.length === 0) {
                    periods = [];
                } else {
                    const firstDate = new Date(trackedValues.reduce((min, tv) => Math.min(min, tv.timestamp), Infinity));
                    const firstPeriodStart = groupByWeek
                        ? startOfWeek(firstDate, { weekStartsOn: 1 })
                        : startOfMonth(firstDate);
                    let currentPeriodStart = groupByWeek
                        ? startOfWeek(now, { weekStartsOn: 1 })
                        : startOfMonth(now);

                    const tempPeriods: { start: number; end: number; label: string }[] = [];
                    let iter = 0;
                    while (currentPeriodStart.getTime() >= firstPeriodStart.getTime() && iter < 100) {
                        const pStart = groupByWeek ? startOfDay(currentPeriodStart) : startOfMonth(currentPeriodStart);
                        const pEnd = groupByWeek ? addDays(currentPeriodStart, 6) : endOfMonth(currentPeriodStart);
                        tempPeriods.push({
                            start: pStart.getTime(),
                            end: pEnd.getTime(),
                            label: groupByWeek
                                ? `Неделя ${format(pStart, "dd.MM")}`
                                : format(pStart, "MMM yyyy", { locale: ru })
                        });
                        currentPeriodStart = groupByWeek
                            ? subDays(currentPeriodStart, 7)
                            : subMonths(currentPeriodStart, 1);
                        iter++;
                    }
                    periods = tempPeriods.reverse();
                }
            } else {
                const periodsCount = 12;
                periods = Array.from({ length: periodsCount }, (_, i) => {
                    const periodEnd = groupByWeek
                        ? subDays(startOfWeek(now, { weekStartsOn: 1 }), i * 7)
                        : subMonths(startOfMonth(now), i);
                    const periodStart = groupByWeek
                        ? startOfDay(periodEnd)
                        : startOfMonth(periodEnd);
                    const periodEndDate = groupByWeek
                        ? addDays(periodEnd, 6)
                        : endOfMonth(periodEnd);

                    return {
                        start: periodStart.getTime(),
                        end: periodEndDate.getTime(),
                        label: groupByWeek
                            ? `Неделя ${format(periodStart, "dd.MM")}`
                            : format(periodStart, "MMM yyyy", { locale: ru })
                    };
                }).reverse();
            }

            const dataPoints = periods.map((period) => {
                const valuesInPeriod = trackedValues.filter(
                    (tv) => tv.timestamp >= period.start && tv.timestamp <= period.end
                );

                if (valuesInPeriod.length === 0) return null;

                const sum = valuesInPeriod.reduce((acc, v) => acc + v.value, 0);

                if (display === "SUM") {
                    return sum;
                } else if (display === "AVG") {
                    return sum / valuesInPeriod.length;
                }
                return null;
            });

            const barColors = dataPoints.map((value, index) => {
                const period = periods[index];
                const isInCurrentPeriod = isCurrentPeriod(period.start);

                if (value === null) {
                    return '#e0e0e0';
                }
                if (isInCurrentPeriod) {
                    return `${lifePartColor}80`;
                }
                if (!hasTarget) return `${lifePartColor}80`;
                return isTargetReached(value) ? '#28a745' : '#dc3545';
            });

            const barBorderColors = dataPoints.map((value, index) => {
                const period = periods[index];
                const isInCurrentPeriod = isCurrentPeriod(period.start);

                if (value === null) {
                    return '#c0c0c0';
                }
                if (isInCurrentPeriod) {
                    return lifePartColor;
                }
                if (!hasTarget) return lifePartColor;
                return isTargetReached(value) ? '#1e7e34' : '#bd2130';
            });

            const datasets: any[] = [
                {
                    label: trackerName,
                    data: dataPoints,
                    backgroundColor: barColors,
                    borderColor: barBorderColors,
                    borderWidth: 1,
                    borderRadius: 4,
                    maxBarThickness: chartMode === "ALL" ? 30 : 50
                }
            ];

            if (hasTarget) {
                datasets.push({
                    type: 'line' as const,
                    label: 'Цель',
                    data: Array(periods.length).fill(target),
                    borderColor: '#6c757d',
                    borderDash: [5, 5],
                    borderWidth: 2,
                    pointRadius: 0,
                    fill: false,
                    tension: 0
                });
            }

            return {
                labels: periods.map((p) => p.label),
                datasets
            };
        }
    }, [trackedValues, trackerName, isSimple, hasTarget, lifePartColor, isTargetReached, chartMode, group, display, isCurrentPeriod, target]);

    const chartOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => `${context.parsed.y} ${measure}`
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: isSimple ? false : true
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: '#e0e0e0'
                }
            }
        }
    };

    return (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">{trackerName}</h6>
                <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setChartMode(chartMode === "LAST14" ? "ALL" : "LAST14")}
                    title={chartMode === "LAST14" ? "Показать все" : "Показать последние 14"}
                >
                    <Icon icon={chartMode === "LAST14" ? faExpand : faCompress} />
                </Button>
            </div>
            <div style={{ height: 200 }}>
                {isSimple ? (
                    <Line data={chartData} options={chartOptions} />
                ) : (
                    <Bar data={chartData} options={chartOptions} />
                )}
            </div>
        </div>
    );
}

const API_BASE = "/api/external-data";

interface TrackerPage {
    id: number;
    slug: string;
    title: string;
    text: string;
    date_created: string;
    date_updated: string;
}

interface TrackerOnPage {
    id: number;
    tracker_id: number;
    order: number;
    custom_title: string;
}

interface TrackerPageFull extends TrackerPage {
    trackers: TrackerOnPage[];
}

interface UserTracker {
    id: number;
    uuid: string;
    data: Record<string, unknown>;
}

export default function SharedTrackersPage() {
    const websocket = useContext(WebSocketContext);
    const navigate = useNavigate();
    const wheelId = useWheelId();
    const dataContext = useContext(DataContext);
    const [pages, setPages] = useState<TrackerPage[]>([]);
    const [selectedPage, setSelectedPage] = useState<TrackerPageFull | null>(null);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddTrackerModal, setShowAddTrackerModal] = useState(false);
    const textEditorRef = useRef<MDXEditorMethods>(null);
    const newTextEditorRef = useRef<MDXEditorMethods>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [newPageTitle, setNewPageTitle] = useState("");
    const [newPageSlug, setNewPageSlug] = useState("");
    const [newPageText, setNewPageText] = useState("");
    const [newSlugAutoGenerated, setNewSlugAutoGenerated] = useState(true);
    const [editTitle, setEditTitle] = useState("");
    const [editSlug, setEditSlug] = useState("");
    const [editText, setEditText] = useState("");
    const [editSlugAutoGenerated, setEditSlugAutoGenerated] = useState(true);
    const [addTrackerId, setAddTrackerId] = useState<number | null>(null);
    const [userTrackers, setUserTrackers] = useState<UserTracker[]>([]);
    const itemsPerPage = 10;

    const isOnline = websocket?.connected ?? false;
    const roles = dataContext?.roles?.objects ?? {};

    const fetchPages = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE}/pages`);
            if (response.ok) {
                const data: TrackerPage[] = await response.json();
                setPages(data);
            }
        } catch (error) {
            console.error("Failed to fetch pages:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchUserTrackers = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE}/trackers`);
            if (response.ok) {
                const data: UserTracker[] = await response.json();
                setUserTrackers(data);
            }
        } catch (error) {
            console.error("Failed to fetch trackers:", error);
        }
    }, []);

    const fetchPageDetails = useCallback(async (pageId: number) => {
        try {
            const response = await fetch(`${API_BASE}/pages/${pageId}`);
            if (response.ok) {
                const data: TrackerPageFull = await response.json();
                setSelectedPage(data);
            }
        } catch (error) {
            console.error("Failed to fetch page details:", error);
        }
    }, []);

    useEffect(() => {
        if (isOnline) {
            fetchPages();
            fetchUserTrackers();
        }
    }, [isOnline, fetchPages, fetchUserTrackers]);

    const filteredPages = useMemo(() => {
        if (!searchTerm) return pages;
        const lower = searchTerm.toLowerCase();
        return pages.filter(p => p.title.toLowerCase().includes(lower) || p.slug.toLowerCase().includes(lower));
    }, [pages, searchTerm]);

    const paginatedPages = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredPages.slice(start, start + itemsPerPage);
    }, [filteredPages, currentPage]);

    const totalPages = Math.ceil(filteredPages.length / itemsPerPage);

    // Автоматическая генерация slug из названия (создание)
    useEffect(() => {
        if (newSlugAutoGenerated && newPageTitle) {
            setNewPageSlug(titleToSlug(newPageTitle));
        }
    }, [newPageTitle, newSlugAutoGenerated]);

    // Автоматическая генерация slug из названия (редактирование)
    useEffect(() => {
        if (editSlugAutoGenerated && editTitle) {
            setEditSlug(titleToSlug(editTitle));
        }
    }, [editTitle, editSlugAutoGenerated]);

    const handleCreatePage = async () => {
        if (!newPageTitle.trim() || !newPageSlug.trim()) return;
        const text = newTextEditorRef.current?.getMarkdown() || "";
        try {
            const response = await fetch(`${API_BASE}/pages`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-Csrftoken": getCsrfToken() },
                body: JSON.stringify({
                    title: newPageTitle,
                    slug: newPageSlug,
                    text: text,
                }),
            });
            if (response.ok) {
                setShowCreateModal(false);
                fetchPages();
            }
        } catch (error) {
            console.error("Failed to create page:", error);
        }
    };

    const handleUpdatePage = async () => {
        if (!selectedPage) return;
        const newText = textEditorRef.current?.getMarkdown() || "";
        try {
            const response = await fetch(`${API_BASE}/pages/${selectedPage.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", "X-Csrftoken": getCsrfToken() },
                body: JSON.stringify({
                    title: editTitle,
                    slug: editSlug,
                    text: newText,
                }),
            });
            if (response.ok) {
                setShowEditModal(false);
                fetchPages();
                if (selectedPage) fetchPageDetails(selectedPage.id);
            }
        } catch (error) {
            console.error("Failed to update page:", error);
        }
    };

    const handleDeletePage = async (pageId: number) => {
        if (!confirm("Вы уверены, что хотите удалить эту страницу?")) return;
        try {
            const response = await fetch(`${API_BASE}/pages/${pageId}`, {
                method: "DELETE",
                headers: { "X-Csrftoken": getCsrfToken() },
            });
            if (response.ok) {
                setSelectedPage(null);
                fetchPages();
            }
        } catch (error) {
            console.error("Failed to delete page:", error);
        }
    };

    const handleAddTrackerToPage = async () => {
        if (!selectedPage || addTrackerId === null) return;
        try {
            const response = await fetch(`${API_BASE}/pages/${selectedPage.id}/trackers`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-Csrftoken": getCsrfToken() },
                body: JSON.stringify({
                    tracker_id: addTrackerId,
                }),
            });
            if (response.ok) {
                setShowAddTrackerModal(false);
                setAddTrackerId(null);
                fetchPageDetails(selectedPage.id);
            }
        } catch (error) {
            console.error("Failed to add tracker:", error);
        }
    };

    const handleRemoveTracker = async (pageId: number, trackId: number) => {
        try {
            const response = await fetch(`${API_BASE}/pages/${pageId}/trackers/${trackId}`, {
                method: "DELETE",
                headers: { "X-Csrftoken": getCsrfToken() },
            });
            if (response.ok && selectedPage) {
                const updatedTrackers = selectedPage.trackers.filter(t => t.id !== trackId);
                setSelectedPage({ ...selectedPage, trackers: updatedTrackers });
                fetchPages();
            }
        } catch (error) {
            console.error("Failed to remove tracker:", error);
        }
    };

    const handleMoveTracker = async (pageId: number, trackId: number, direction: -1 | 1) => {
        if (!selectedPage) return;
        const sorted = [...selectedPage.trackers].sort((a, b) => a.order - b.order);
        const idx = sorted.findIndex(t => t.id === trackId);
        if (idx === -1) return;
        const newIdx = idx + direction;
        if (newIdx < 0 || newIdx >= sorted.length) return;

        const [moved] = sorted.splice(idx, 1);
        sorted.splice(newIdx, 0, moved);

        const updatedTrackers = sorted.map((t, i) => ({ ...t, order: i }));
        setSelectedPage({ ...selectedPage, trackers: updatedTrackers });

        try {
            const response = await fetch(`${API_BASE}/pages/${pageId}/trackers/${trackId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", "X-Csrftoken": getCsrfToken() },
                body: JSON.stringify({
                    order: updatedTrackers.find(t => t.id === trackId)?.order,
                }),
            });
            if (!response.ok) {
                fetchPageDetails(pageId);
            }
        } catch (error) {
            console.error("Failed to move tracker:", error);
            fetchPageDetails(pageId);
        }
    };

    const handlePageClick = (page: TrackerPage) => {
        setSelectedPage(null);
        fetchPageDetails(page.id);
    };

    const handleGoBack = () => {
        setSelectedPage(null);
    };

    const [copiedPageSlug, setCopiedPageSlug] = useState<string | null>(null);

    const shareUrl = (slug: string) => {
        return `${window.location.origin}/shared/trackers/${slug}`;
    };

    const handleCopyLink = (slug: string) => {
        navigator.clipboard.writeText(shareUrl(slug));
        setCopiedPageSlug(slug);
        setTimeout(() => setCopiedPageSlug(null), 3000);
    };

    if (!isOnline) {
        return (
            <Container fluid>
                <Row>
                    <Col>
                        <div className="mt-4">
                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
                                        <Icon icon={faArrowLeft} />
                                    </Button>
                                    <h2 className="mb-0">Опубликованные трекеры</h2>
                                </div>
                            </div>
                            <Alert variant="warning" className="text-center">
                                <Icon icon={faExclamationTriangle} className="me-2" />
                                Раздел доступен только при подключении к интернету. Проверьте соединение и обновите страницу.
                            </Alert>
                        </div>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Row>
                <Col>
                    <div className="mt-4">
                        {!selectedPage && (
                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                <h2 className="mb-0">Опубликованные трекеры</h2>
                                <Button variant="outline-success" onClick={() => {
                                    setNewPageTitle("");
                                    setNewPageSlug("");
                                    setNewPageText("");
                                    setNewSlugAutoGenerated(true);
                                    setShowCreateModal(true);
                                }}>
                                    <Icon icon={add} className="me-2" />
                                    Создать страницу
                                </Button>
                            </div>
                        )}
                        {!selectedPage && (
                            <nav aria-label="breadcrumb" className="mb-3">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <a href="#" onClick={(e) => { e.preventDefault(); navigate(`/${wheelId}/growth`); }}>Трекеры</a>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">Опубликованные</li>
                                </ol>
                            </nav>
                        )}

                        {loading ? (
                            <div className="text-center py-4">Загрузка...</div>
                        ) : selectedPage ? (
                            <div>
                                <nav aria-label="breadcrumb" className="mb-3">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item">
                                            <a href="#" onClick={(e) => { e.preventDefault(); navigate(`/${wheelId}/growth`); }}>Трекеры</a>
                                        </li>
                                        <li className="breadcrumb-item">
                                            <a href="#" onClick={(e) => { e.preventDefault(); setSelectedPage(null); }}>Опубликованные</a>
                                        </li>
                                        <li className="breadcrumb-item active" aria-current="page">{selectedPage.title}</li>
                                    </ol>
                                </nav>
                                <Card>
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h5 className="mb-0">{selectedPage.title}</h5>
                                            <small className="text-muted">
                                                slug: {selectedPage.slug} | Создана: {new Date(selectedPage.date_created).toLocaleDateString('ru-RU')}
                                            </small>
                                        </div>
                                        <div className="d-flex gap-2">
                                        <Button variant="outline-secondary" size="sm" onClick={() => {
                                            setEditTitle(selectedPage.title);
                                            setEditSlug(selectedPage.slug);
                                            setEditText(selectedPage.text);
                                            setEditSlugAutoGenerated(true);
                                            setShowEditModal(true);
                                        }}>
                                            <Icon icon={faEdit} />
                                        </Button>
                                        <Button variant="outline-danger" size="sm" onClick={() => handleDeletePage(selectedPage.id)}>
                                            <Icon icon={faTrash} />
                                        </Button>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {selectedPage.text && (
                                        <div className="mb-3">
                                            <Markdown remarkPlugins={[remarkGfm]}>{selectedPage.text}</Markdown>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-end mb-3">
                                        <Button variant="outline-success" size="sm" onClick={() => {
                                            setAddTrackerId(null);
                                            setShowAddTrackerModal(true);
                                        }}>
                                            <Icon icon={add} className="me-1" />
                                            Добавить трекер
                                        </Button>
                                    </div>
                                    {selectedPage.trackers.length === 0 ? (
                                        <Alert variant="info">Трекеров пока нет. Добавьте первый трекер.</Alert>
                                    ) : (
                                        <>
                                            {selectedPage.trackers
                                                .sort((a, b) => a.order - b.order)
                                                .map((t) => {
                                                    const userTracker = userTrackers.find(tr => tr.id === t.tracker_id);
                                                    const trackerData = userTracker?.data as Record<string, unknown> || {};
                                                    const trackerName = (trackerData.name as string) || `Трекер #${t.tracker_id}`;
                                                    const trackerUuid = (trackerData.uuid as string) || userTracker?.uuid;
                                                    const lifePartColor = (trackerData.lifepart as string) ? roles[(trackerData.lifepart as string)]?.color : undefined;
                                                    const measure = (trackerData.measure as string) || '';
                                                    const display = trackerData.display as 'SIMPLE' | 'SUM' | 'AVG' | undefined;
                                                    const group = trackerData.group as 'NO' | 'WEEK' | 'MONTH' | undefined;
                                                    const target = trackerData.target as number | undefined;
                                                    const targetIndicator = trackerData.targetIndicator as 'MORE' | 'LESS' | undefined;
                                                    const chartMode = trackerData.chartMode as 'LAST14' | 'ALL' | undefined;
                                                    
                                                    if (!trackerUuid || !lifePartColor) return null;
                                                    
                                                    return (
                                                        <div key={t.id} className="mb-3">
                                                            <div className="d-flex justify-content-end mb-2 gap-1">
                                                                <Button variant="outline-secondary" size="sm" onClick={() => handleMoveTracker(selectedPage.id, t.id, -1)} disabled={t.order === 0}>
                                                                    <Icon icon={faArrowUp} />
                                                                </Button>
                                                                <Button variant="outline-secondary" size="sm" onClick={() => handleMoveTracker(selectedPage.id, t.id, 1)} disabled={t.order === selectedPage.trackers.length - 1}>
                                                                    <Icon icon={faArrowDown} />
                                                                </Button>
                                                                <Button variant="outline-danger" size="sm" onClick={() => handleRemoveTracker(selectedPage.id, t.id)}>
                                                                    <Icon icon={faTrash} />
                                                                </Button>
                                                            </div>
                                                            <TrackerChart
                                                                trackerUuid={trackerUuid}
                                                                lifePartColor={lifePartColor}
                                                                trackerName={t.custom_title || trackerName}
                                                                measure={measure}
                                                                target={target}
                                                                targetIndicator={targetIndicator}
                                                                display={display}
                                                                group={group}
                                                                chartMode={chartMode}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                        </>
                                    )}
                                </Card.Body>
                                <Card.Footer>
                                    <Button variant="outline-info" size="sm" onClick={() => handleCopyLink(selectedPage.slug)}>
                                        <Icon icon={faShareNodes} className="me-1" />
                                        {copiedPageSlug === selectedPage.slug ? 'Скопировано' : 'Скопировать ссылку'}
                                    </Button>
                                </Card.Footer>
                            </Card>
                            </div>
                        ) : (
                            <>
                                <div className="mb-3">
                                    <Form.Control
                                        type="text"
                                        placeholder="Поиск по названию или slug..."
                                        value={searchTerm}
                                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                    />
                                </div>
                                <Card>
                                    <Card.Body>
                                        {filteredPages.length === 0 ? (
                                            <Alert variant="info">
                                                {searchTerm ? "Ничего не найдено." : "Страниц трекеров пока нет. Создайте первую!"}
                                            </Alert>
                                        ) : (
                                            <>
                                                <Table striped bordered hover>
                                                    <thead>
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Название</th>
                                                            <th>Ссылка</th>
                                                            <th>Создана</th>
                                                            <th>Обновлена</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginatedPages.map((page, idx) => (
                                                            <tr key={page.id} style={{ cursor: "pointer" }} onClick={() => handlePageClick(page)}>
                                                                <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                                                                <td>{page.title}</td>
                                                                <td>
                                                                    <Button variant="outline-secondary" size="sm" onClick={(e) => { e.stopPropagation(); handleCopyLink(page.slug); }}>
                                                                        <Icon icon={faShareNodes} className="me-1" />
                                                                        {copiedPageSlug === page.slug ? 'Скопировано' : 'Копировать'}
                                                                    </Button>
                                                                </td>
                                                                <td>{new Date(page.date_created).toLocaleDateString('ru-RU')}</td>
                                                                <td>{new Date(page.date_updated).toLocaleDateString('ru-RU')}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                                {totalPages > 1 && (
                                                    <div className="d-flex justify-content-center mt-3">
                                                        <nav>
                                                            <ul className="pagination mb-0">
                                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                    <button
                                                                        className="page-link"
                                                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                                    >
                                                                        Назад
                                                                    </button>
                                                                </li>
                                                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                                                                    <li
                                                                        key={pageNum}
                                                                        className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                                                                    >
                                                                        <button
                                                                            className="page-link"
                                                                            onClick={() => setCurrentPage(pageNum)}
                                                                        >
                                                                            {pageNum}
                                                                        </button>
                                                                    </li>
                                                                ))}
                                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                    <button
                                                                        className="page-link"
                                                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                                    >
                                                                        Вперёд
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                        </nav>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            </>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Модальное окно создания страницы */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Создать страницу трекеров</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Название</Form.Label>
                            <Form.Control
                                type="text"
                                value={newPageTitle}
                                onChange={(e) => setNewPageTitle(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Slug (URL)</Form.Label>
                            <Form.Control
                                type="text"
                                value={newPageSlug}
                                onChange={(e) => { setNewPageSlug(e.target.value); setNewSlugAutoGenerated(false); }}
                                placeholder="my-trackers"
                                required
                            />
                            <Form.Text className="text-muted">Автоматически из названия</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Описание (Markdown)</Form.Label>
                            <ForwardRefEditor
                                markdown={newPageText}
                                ref={newTextEditorRef}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                        Отмена
                    </Button>
                    <Button variant="success" onClick={handleCreatePage} disabled={!newPageTitle.trim() || !newPageSlug.trim()}>
                        Создать
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Модальное окно редактирования страницы */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Редактировать страницу</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Название</Form.Label>
                            <Form.Control
                                type="text"
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Slug (URL)</Form.Label>
                            <Form.Control
                                type="text"
                                value={editSlug}
                                onChange={(e) => { setEditSlug(e.target.value); setEditSlugAutoGenerated(false); }}
                                required
                            />
                            <Form.Text className="text-muted">Автоматически из названия</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Описание (Markdown)</Form.Label>
                            <ForwardRefEditor
                                markdown={editText}
                                ref={textEditorRef}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Отмена
                    </Button>
                    <Button variant="primary" onClick={handleUpdatePage}>
                        Сохранить
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Модальное окно добавления трекера */}
            <Modal show={showAddTrackerModal} onHide={() => setShowAddTrackerModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Добавить трекер</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Выберите трекер</Form.Label>
                        <Form.Select
                            value={addTrackerId ?? ""}
                            onChange={(e) => setAddTrackerId(Number(e.target.value) || null)}
                            required
                        >
                            <option value="">— Выберите трекер —</option>
                            {userTrackers.map((tracker) => {
                                const trackerData = tracker.data as Record<string, unknown> || {};
                                const trackerName = (trackerData.name as string) || `Трекер #${tracker.id}`;
                                return (
                                    <option key={tracker.id} value={tracker.id}>
                                        {trackerName}
                                    </option>
                                );
                            })}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddTrackerModal(false)}>
                        Отмена
                    </Button>
                    <Button variant="success" onClick={handleAddTrackerToPage} disabled={addTrackerId === null}>
                        Добавить
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
